import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Loading, Title, Subtitle, BlueButton } from '@carvana/purchase-styles';
import { CartIcon, CheckmarkIcon, UnLockedIcon } from '@carvana/purchase-icons';
import { DetermineNextStep } from 'Components/dashboard/ducks/actions';

const Content = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0rem;

  @media (min-width: ${props => props.theme.breakpoints.md}px) {
    padding: 2rem 5rem;
  }
`;

const HelperText = styled.p`
  font-size: 1.2rem;
  font-weight: 700;
`;

const Cart = styled(CartIcon)`
  width: 1.1rem;
  height: 1.1rem;
  fill: white;
  padding-right: 0.5rem;
`;

const BlueCheckmark = styled(CheckmarkIcon)`
  width: 2.5rem;
  height: 2.5rem;
  fill: ${props => props.theme.carvana.blue};
`;

const Unlocked = styled(UnLockedIcon)`
  width: 2.5rem;
  height: 2.5rem;
`;

const ButtonWrapper = styled.div`
  width: 30rem;
`;

class DashboardLanding extends PureComponent {
  static propTypes = {
    isMobile: PropTypes.bool.isRequired,
    routeTo: PropTypes.func.isRequired,
    mobileRouteToDashboard: PropTypes.bool.isRequired,
    toggleMobileRouteToDashboard: PropTypes.func.isRequired,
    navigationConfiguration: PropTypes.shape({
      navItems: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    steps: PropTypes.arrayOf(PropTypes.shape({})),
    lockTime: PropTypes.shape({}).isRequired,
    location: PropTypes.shape({}).isRequired,
    name: PropTypes.string.isRequired,
    toggleSummary: PropTypes.func.isRequired
  };

  static defaultProps = {
    navigationConfiguration: {
      navItems: []
    },
    steps: null
  };

  state = {
    isLoading: false
  };

  componentDidMount() {
    this.setState({ isLoading: true });
    const {
      navigationConfiguration: {
        navItems,
      },
      routeTo,
      toggleMobileRouteToDashboard,
      mobileRouteToDashboard,
      isMobile
    } = this.props;

    const nextStep = DetermineNextStep(navItems);

    if (isMobile && mobileRouteToDashboard) {
      this.setState({ isLoading: false });
      toggleMobileRouteToDashboard(false);
      return;
    }

    const disableGuidedFlow =
      sessionStorage.getItem('CVDisableGuidedFlow') === 'true';

    if (nextStep?.route && !disableGuidedFlow) {
      routeTo(nextStep?.route, 'replace');
    }

    if (disableGuidedFlow) this.setState({ isLoading: false });
  }

  componentDidUpdate() {
    const { routeTo, isMobile, steps } = this.props;

    if (!isMobile && steps) routeTo('/dashboard-reload', 'replace');
  }

  render() {
    const {
      isMobile,
      steps,
      lockTime: { isExpired: isLockTimerExpired } = {},
      name,
      toggleSummary
    } = this.props;
    const { isLoading } = this.state;
    const enabledSteps = steps?.filter(x => x?.isEnabled);
    const totalSteps = enabledSteps?.length || 0;
    const completedSteps =
      enabledSteps?.filter(x => x?.status?.toLowerCase() === 'complete')
        ?.length || 0;
    const needsAttentionSteps =
      enabledSteps?.filter(x => x?.status?.toLowerCase() === 'needsattention')
        ?.length || 0;
    const hasCompletedAllSteps = totalSteps === completedSteps;

    return (
      <Fragment>
        {!isMobile && (
          <Fragment>
            {isLoading && <Loading />}

            {!isLoading && totalSteps === 0 && (
              <Fragment>
                <Loading />
                <div data-cv-test="dashboardLanding" />
              </Fragment>
            )}

            {!isLoading && totalSteps > 0 && (
              <Fragment>
                <Title>Hello{name && `, ${name.split(' ')[0]}`}</Title>

                <div data-cv-test="dashboardLanding" />

                <Content>
                  {!isLockTimerExpired && (
                    <Fragment>
                      <BlueCheckmark />

                      {!hasCompletedAllSteps && (
                        <Subtitle>
                          You have completed {completedSteps} out of{' '}
                          {totalSteps} steps
                          {needsAttentionSteps > 0 &&
                            `, and ${needsAttentionSteps} step(s) need your attention`}
                          ! Complete more steps to earn more lock time on this
                          purchase.
                        </Subtitle>
                      )}

                      {hasCompletedAllSteps && (
                        <Subtitle>
                          You have successfully completed all the steps on the
                          dashboard! Next, let&#39;s review your purchase
                          summary.
                        </Subtitle>
                      )}
                    </Fragment>
                  )}

                  {isLockTimerExpired && (
                    <Fragment>
                      <Unlocked />

                      {hasCompletedAllSteps && (
                        <Subtitle>
                          You have successfully completed all the steps on the
                          dashboard! You don&#39;t have any lock time remaining,
                          so we need you to finalize your purchase soon in order
                          to secure your new car!
                        </Subtitle>
                      )}

                      {!hasCompletedAllSteps && (
                        <Subtitle>
                          Your car is unlocked! Complete additional steps now to
                          make sure the vehicle isn&#39;t purchased by someone
                          else!
                        </Subtitle>
                      )}
                    </Fragment>
                  )}

                  <HelperText>
                    Continue with any step on the dashboard, or
                  </HelperText>

                  <ButtonWrapper>
                    <BlueButton onClick={toggleSummary}>
                      <Cart />
                      View Purchase Summary
                    </BlueButton>
                  </ButtonWrapper>
                </Content>
              </Fragment>
            )}
          </Fragment>
        )}
      </Fragment>
    );
  }
}

export default DashboardLanding;
